import React, { useState } from 'react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { GripVertical, Trash2, ChevronDown, ChevronUp } from 'lucide-react';
import { OPERATORS } from './ruleBuilderData';

export const DraggableCondition = ({ condition, index, onUpdate, onRemove, onMove, variables, isFirst, isLast }) => {
  const [isDragging, setIsDragging] = useState(false);
  const variable = variables.find(v => v.key === condition.var);

  const handleDragStart = (e) => {
    e.dataTransfer.setData('conditionIndex', index.toString());
    setIsDragging(true);
  };

  const handleDragEnd = () => setIsDragging(false);

  const handleDrop = (e) => {
    e.preventDefault();
    const fromIndex = parseInt(e.dataTransfer.getData('conditionIndex'));
    if (fromIndex !== index) {
      onMove(fromIndex, index);
    }
  };

  return (
    <div
      draggable
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragOver={(e) => e.preventDefault()}
      onDrop={handleDrop}
      className={`flex items-center gap-2 p-3 rounded-lg border-2 transition-all ${
        isDragging ? 'border-blue-400 bg-blue-50 opacity-50' : 'border-gray-200 bg-white hover:border-gray-300'
      }`}
    >
      <div className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600">
        <GripVertical className="w-5 h-5" />
      </div>

      {/* Variable selector */}
      <select
        value={condition.var || ''}
        onChange={(e) => onUpdate(index, 'var', e.target.value)}
        className="flex-1 min-w-[140px] border rounded-md p-2 text-sm bg-white focus:ring-2 focus:ring-blue-500"
      >
        <option value="">Variable...</option>
        {variables.map(v => (
          <option key={v.key} value={v.key}>{v.label}</option>
        ))}
      </select>

      {/* Operator selector */}
      <select
        value={condition.op || '=='}
        onChange={(e) => onUpdate(index, 'op', e.target.value)}
        className="w-[100px] border rounded-md p-2 text-sm bg-white focus:ring-2 focus:ring-blue-500"
      >
        {OPERATORS.map(op => (
          <option key={op.value} value={op.value}>{op.symbol} {op.label.split('(')[0]}</option>
        ))}
      </select>

      {/* Value input */}
      {variable?.values ? (
        <select
          value={condition.value || ''}
          onChange={(e) => onUpdate(index, 'value', e.target.value)}
          className="flex-1 min-w-[140px] border rounded-md p-2 text-sm bg-white focus:ring-2 focus:ring-blue-500"
        >
          <option value="">Valeur...</option>
          {variable.values.map(v => (
            <option key={v} value={v}>{v}</option>
          ))}
        </select>
      ) : (
        <Input
          type={variable?.type === 'number' ? 'number' : 'text'}
          value={condition.value ?? ''}
          onChange={(e) => onUpdate(index, 'value', 
            variable?.type === 'number' ? parseFloat(e.target.value) || 0 : e.target.value
          )}
          placeholder="Valeur"
          className="flex-1 min-w-[100px]"
        />
      )}

      {/* Move buttons */}
      <div className="flex flex-col gap-0.5">
        <button
          onClick={() => onMove(index, index - 1)}
          disabled={isFirst}
          className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
        >
          <ChevronUp className="w-4 h-4" />
        </button>
        <button
          onClick={() => onMove(index, index + 1)}
          disabled={isLast}
          className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
        >
          <ChevronDown className="w-4 h-4" />
        </button>
      </div>

      {/* Remove button */}
      <Button
        variant="ghost"
        size="sm"
        onClick={() => onRemove(index)}
        className="text-red-500 hover:text-red-700 hover:bg-red-50"
      >
        <Trash2 className="w-4 h-4" />
      </Button>
    </div>
  );
};
